import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  createContext,
  useMemo,
} from "react";
import {
  type AccountInfo,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalInstance, AUTH_SCOPE } from "../config/auth";
import { createGraphQLClient } from "../lib/graphql";
import { UserRole, DEFAULT_ROLE, AZURE_AD_ROLE_MAP } from "../types/roles";

interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  userName: string | null;
  userRole: UserRole;
  error: Error | null;
  login: () => Promise<void>;
  logout: () => void;
  getAccessToken: (scope: string) => Promise<string | null>;
}

const AuthContext = createContext<UseAuthResult | null>(null);

function extractRole(claims?: Record<string, unknown>): UserRole {
  const roles = claims?.roles;
  if (!roles) return DEFAULT_ROLE;

  const roleArray = Array.isArray(roles) ? roles : [roles];

  // Purchase > Sales (Purchase has access to everything)
  for (const role of roleArray) {
    if (AZURE_AD_ROLE_MAP[role as string] === "Purchase") return "Purchase";
  }
  for (const role of roleArray) {
    if (AZURE_AD_ROLE_MAP[role as string] === "Sales") return "Sales";
  }
  return DEFAULT_ROLE;
}

async function acquireTokenSilently(
  account: AccountInfo,
  scopes: string[],
): Promise<string | null> {
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes, account });
      return null;
    }
    return null;
  }
}

function useAuthInternal(): UseAuthResult {
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const userName = useMemo(() => {
    const claims = account?.idTokenClaims;
    return (
      (claims?.name as string) ?? (claims?.preferred_username as string) ?? null
    );
  }, [account]);

  const userRole = useMemo(
    () => extractRole(account?.idTokenClaims),
    [account],
  );

  // Initialize GraphQL client with an async token getter
  useEffect(() => {
    createGraphQLClient(async () => {
      const active = account ?? msalInstance.getAllAccounts()[0];

      if (!active) {
        return null;
      }

      return acquireTokenSilently(active, [AUTH_SCOPE]);
    });
  }, [account]);

  // Attempt silent auth on mount from MSAL cache
  useEffect(() => {
    const init = async () => {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        try {
          const result = await msalInstance.acquireTokenSilent({
            scopes: [AUTH_SCOPE],
            account: accounts[0],
          });
          setAccount(result.account);
        } catch {
          // Cached session expired or unusable — user must log in again
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const login = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await msalInstance.loginRedirect({ scopes: [AUTH_SCOPE] });
    } catch (err) {
      setError(err as Error);
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setAccount(null);
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/login`,
    });
  }, []);

  const getAccessToken = useCallback(
    async (scope: string): Promise<string | null> => {
      const active = account ?? msalInstance.getAllAccounts()[0];
      if (!active) return null;
      return acquireTokenSilently(active, [scope]);
    },
    [account],
  );

  return {
    isAuthenticated: !!account,
    isLoading,
    userName,
    userRole,
    error,
    login,
    logout,
    getAccessToken,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuthInternal();
  return React.createElement(AuthContext.Provider, { value: auth }, children);
};

export function useAuth(): UseAuthResult {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
